'use client'

import { useState } from 'react'
import type { FooterType } from '@/lib/types/global-content'
import { isValidEmail } from '@/lib/contact/contact-form-utils'
import { getRecaptchaSiteKey, getRecaptchaToken, loadRecaptchaScript } from '@/lib/contact/recaptcha'
import { submitSubscribeForm } from '@/lib/contact/submit-contact-form'
import { isLayoutB } from '@/lib/utils/section-styles'

type SubscribeData = NonNullable<FooterType['subscribe']>
type SubmitState = 'idle' | 'loading' | 'success' | 'error'

const EMAIL_VALIDATION_MESSAGE = 'Please enter a valid email address.'

type FooterSubscribeProps = {
  data: SubscribeData
  layout?: string | null
}

function validateEmail(email: string): string | null {
  const trimmed = email.trim()
  if (!trimmed || !isValidEmail(trimmed)) {
    return EMAIL_VALIDATION_MESSAGE
  }
  return null
}

/** Same brand fill + brand-dark text as contact form submit. */
function subscribeButtonClass(layoutB: boolean): string {
  const colors =
    'border border-transparent bg-brand-primary text-brand-dark hover:opacity-90 focus-visible:ring-2 focus-visible:ring-brand-primary'
  if (layoutB) {
    return `shrink-0 cursor-pointer rounded-r-[2px] rounded-l-none px-4 py-[15px] font-body text-[15px] font-semibold leading-5 transition-opacity focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60 md:py-[14px] md:text-[14px] md:font-bold whitespace-nowrap ${colors}`
  }

  return `shrink-0 cursor-pointer rounded-r-pill rounded-l-none px-4 py-[14px] md:py-[14px] text-[15px] md:text-[14px] leading-5 font-body font-semibold md:font-bold whitespace-nowrap transition-opacity focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60 ${colors}`
}

export default function FooterSubscribe({ data, layout }: FooterSubscribeProps) {
  const [email, setEmail] = useState('')
  const [submitState, setSubmitState] = useState<SubmitState>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const recaptchaSiteKey = getRecaptchaSiteKey()
  const layoutB = isLayoutB(layout)

  const prefetchRecaptcha = () => {
    if (!recaptchaSiteKey) return
    loadRecaptchaScript(recaptchaSiteKey).catch(() => {
      // Prefetch failure is non-fatal; submit will surface an error if needed.
    })
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setErrorMsg('')

    const validationError = validateEmail(email)
    if (validationError) {
      setSubmitState('error')
      setErrorMsg(validationError)
      return
    }

    setSubmitState('loading')

    try {
      if (!recaptchaSiteKey) {
        throw new Error('reCAPTCHA is not configured. Please try again later.')
      }

      const recaptchaToken = await getRecaptchaToken(recaptchaSiteKey, 'subscribe_form')
      await submitSubscribeForm(email.trim(), recaptchaToken)
      setSubmitState('success')
      setEmail('')
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Subscription failed. Please try again.')
      setSubmitState('error')
    }
  }

  return (
    <div
      className={
        layoutB
          ? 'flex w-full flex-col gap-4 md:w-[336px] md:shrink-0 md:pb-4'
          : 'flex w-full flex-col gap-4 md:w-[336px] md:shrink-0 md:gap-4'
      }
    >
      {data.heading && (
        <p className="hidden font-body text-[12px] font-bold uppercase leading-4 tracking-[2px] text-white md:block">
          {data.heading}
        </p>
      )}
      <div className={`flex flex-col ${layoutB ? 'gap-4 md:gap-0' : 'gap-4'}`}>
        {data.description && (
          <p
            className={
              layoutB
                ? 'hidden font-body text-[14px] leading-5 text-white opacity-60 whitespace-pre-line md:block md:pt-2'
                : 'hidden font-body text-[14px] leading-5 text-white opacity-60 whitespace-pre-line md:block'
            }
          >
            {data.description}
          </p>
        )}
        {submitState === 'success' ? (
          <p
            role="status"
            aria-live="polite"
            className="font-body text-[14px] leading-5 text-white/80"
          >
            Thank you for subscribing!
          </p>
        ) : (
          <form
            onSubmit={handleSubmit}
            onFocus={prefetchRecaptcha}
            className={`relative flex w-full flex-col ${layoutB ? 'md:mt-4' : ''}`}
            aria-label="Email subscription form"
            noValidate
          >
            <div
              className={
                layoutB
                  ? 'flex w-full items-stretch overflow-hidden rounded-[2px] border border-white/20 bg-white/10'
                  : 'flex w-full items-stretch overflow-hidden rounded-pill border border-white/20 bg-white/10'
              }
            >
              <label htmlFor="footer-subscribe-email" className="sr-only">
                Email address (required)
              </label>
              <input
                id="footer-subscribe-email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  if (submitState === 'error') {
                    setSubmitState('idle')
                    setErrorMsg('')
                  }
                }}
                placeholder={data.placeHolderText ?? 'Email Address'}
                required
                autoComplete="email"
                disabled={submitState === 'loading'}
                aria-invalid={submitState === 'error'}
                aria-describedby={submitState === 'error' ? 'footer-subscribe-email-error' : undefined}
                className={
                  layoutB
                    ? 'footer-subscribe-email min-w-0 flex-1 rounded-none border-0 border-r-0 bg-transparent px-3 py-[14px] font-body text-base leading-5 text-white placeholder:text-base placeholder:text-white/50 focus:outline-none disabled:opacity-60 md:px-4 md:py-[13px] md:text-[14px] md:placeholder:text-[14px]'
                    : 'footer-subscribe-email min-w-0 flex-1 rounded-none border-0 bg-transparent px-3 py-[14px] pl-3 font-body text-base leading-5 text-white placeholder:text-base placeholder:text-white/50 focus:outline-none disabled:opacity-60 md:px-5 md:text-[14px] md:placeholder:text-[14px]'
                }
              />
              <button
                type="submit"
                disabled={submitState === 'loading'}
                aria-busy={submitState === 'loading'}
                className={subscribeButtonClass(layoutB)}
              >
                {submitState === 'loading' ? 'Sending…' : (data.buttonLabel ?? 'Subscribe')}
              </button>
            </div>
            {submitState === 'error' && errorMsg && (
              <p
                id="footer-subscribe-email-error"
                role="alert"
                aria-live="polite"
                className="mt-1 font-body text-nav text-[var(--color-error)]"
              >
                {errorMsg}
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  )
}
